import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { ReturnToStationResponse } from "../../app/models/responseModels/returnToStationResponse";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import RTSAutoCompleteFilter from "./RTSAutoCompleteFilter";
import { DatePicker } from "@mui/x-date-pickers";
import { addDays } from "date-fns";
import {
  RTS_COLUMN_NAME,
  RTS_FILTER_COLUMN_OPTIONS,
  RTS_LABEL_NAME,
} from "./RTSConstants";

const COLUMN_WITH_DATE = ["statusDate", "deliveryDate", "updatedOn"];

type RTSDataFilterProps = {
  returnToStationData: ReturnToStationResponse[];
  setFilteredData: React.Dispatch<
    React.SetStateAction<ReturnToStationResponse[]>
  >;
  filteredData: ReturnToStationResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const RTSDataFilter = ({
  returnToStationData,
  setFilteredData,
  filteredData,
}: RTSDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = React.useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [trackingNumberFilter, setTrackingNumberFilter] = useState<string>("");

  const [clientNameOptions, setClientNameOptions] = useState<string[]>([]); //Dropdown Options
  const [clientNameSelectedOptions, setClientNameSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [parcelCenterOptions, setParcelCenterOptions] = useState<string[]>([]); //Dropdown Options
  const [parcelCenterSelectedOptions, setParcelCenterSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [statusOptions, setStatusOptions] = useState<string[]>([]); //Dropdown Options
  const [statusSelectedOptions, setStatusSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [recipientCityOptions, setRecipientCityOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [recipientCitySelectedOptions, setRecipientCitySelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [recipientStateOptions, setRecipientStateOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [recipientStateSelectedOptions, setRecipientStateSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [recipientZipOptions, setRecipientZipOptions] = useState<string[]>([]); //Dropdown Options
  const [recipientZipSelectedOptions, setRecipientZipSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [statusFromDate, setStatusFromDate] = useState<Date | null>(null);
  const [statusToDate, setStatusToDate] = useState<Date | null>(null);

  const [deliveryFromDate, setDeliveryFromDate] = useState<Date | null>(null);
  const [deliveryToDate, setDeliveryToDate] = useState<Date | null>(null);

  const [updatedOnFromDate, setUpdatedOnFromDate] = useState<Date | null>(null);
  const [updatedOnToDate, setUpdatedOnToDate] = useState<Date | null>(null);

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === RTS_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        return parcelCenterOptions;
      }
      if (columnName === RTS_COLUMN_NAME.CLIENT_NAME) {
        return clientNameOptions;
      }
      if (columnName === RTS_COLUMN_NAME.PACKAGE_STATUS) {
        return statusOptions;
      }
      if (columnName === RTS_COLUMN_NAME.RECIPIENT_CITY) {
        return recipientCityOptions;
      }
      if (columnName === RTS_COLUMN_NAME.RECIPIENT_STATE) {
        return recipientStateOptions;
      }
      if (columnName === RTS_COLUMN_NAME.RECIPIENT_ZIP) {
        return recipientZipOptions;
      }
      return [];
    },
    [
      parcelCenterOptions,
      clientNameOptions,
      statusOptions,
      recipientCityOptions,
      recipientStateOptions,
      recipientZipOptions,
    ]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === RTS_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        return parcelCenterSelectedOptions;
      }
      if (columnName === RTS_COLUMN_NAME.CLIENT_NAME) {
        return clientNameSelectedOptions;
      }
      if (columnName === RTS_COLUMN_NAME.PACKAGE_STATUS) {
        return statusSelectedOptions;
      }
      if (columnName === RTS_COLUMN_NAME.RECIPIENT_CITY) {
        return recipientCitySelectedOptions;
      }
      if (columnName === RTS_COLUMN_NAME.RECIPIENT_STATE) {
        return recipientStateSelectedOptions;
      }
      if (columnName === RTS_COLUMN_NAME.RECIPIENT_ZIP) {
        return recipientZipSelectedOptions;
      }
      return [];
    },
    [
      parcelCenterSelectedOptions,
      clientNameSelectedOptions,
      statusSelectedOptions,
      recipientCitySelectedOptions,
      recipientStateSelectedOptions,
      recipientZipSelectedOptions,
    ]
  );

  const getSelectedFromDate = useCallback(
    (columnName: string) => {
      if (columnName === RTS_COLUMN_NAME.STATUS_DATE) {
        return statusFromDate;
      }
      if (columnName === RTS_COLUMN_NAME.LAST_SCHEDULED_DELIVERY) {
        return deliveryFromDate;
      }
      if (columnName === RTS_COLUMN_NAME.UPDATED_ON) {
        return updatedOnFromDate;
      }
      return null;
    },
    [statusFromDate, deliveryFromDate, updatedOnFromDate]
  );

  const getSelectedToDate = useCallback(
    (columnName: string) => {
      if (columnName === RTS_COLUMN_NAME.STATUS_DATE) {
        return statusToDate;
      }
      if (columnName === RTS_COLUMN_NAME.LAST_SCHEDULED_DELIVERY) {
        return deliveryToDate;
      }
      if (columnName === RTS_COLUMN_NAME.UPDATED_ON) {
        return updatedOnToDate;
      }
      return null;
    },
    [statusToDate, deliveryToDate, updatedOnToDate]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //Parcel Center dropdown options
    if (selectedColumnName === RTS_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      setParcelCenterOptions(
        returnToStationData
          .map((v) => v.parcelCenterLocationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setParcelCenterOptions(
        filteredData
          .map((v) => v.parcelCenterLocationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //ClientName dropdown options
    if (selectedColumnName === RTS_COLUMN_NAME.CLIENT_NAME) {
      setClientNameOptions(
        returnToStationData
          .map((v) => v.shipmentClientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setClientNameOptions(
        filteredData
          .map((v) => v.shipmentClientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Status dropdown options
    if (selectedColumnName === RTS_COLUMN_NAME.PACKAGE_STATUS) {
      setStatusOptions(
        returnToStationData
          .map((v) => v.status)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setStatusOptions(
        filteredData
          .map((v) => v.status)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //RecipientCity dropdown options
    if (selectedColumnName === RTS_COLUMN_NAME.RECIPIENT_CITY) {
      setRecipientCityOptions(
        returnToStationData
          .map((v) => v.shipmentRecipientCity)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientCityOptions(
        filteredData
          .map((v) => v.shipmentRecipientCity)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //RecipientState dropdown options
    if (selectedColumnName === RTS_COLUMN_NAME.RECIPIENT_STATE) {
      setRecipientStateOptions(
        returnToStationData
          .map((v) => v.shipmentRecipientState)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientStateOptions(
        filteredData
          .map((v) => v.shipmentRecipientState)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //RecipientZip dropdown options
    if (selectedColumnName === RTS_COLUMN_NAME.RECIPIENT_ZIP) {
      setRecipientZipOptions(
        returnToStationData
          .map((v) => v.shipmentRecipientZip)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientZipOptions(
        filteredData
          .map((v) => v.shipmentRecipientZip)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, returnToStationData]);

  useEffect(() => {
    let newData = [...returnToStationData];
    //Parcel Center Filter
    if (parcelCenterSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return parcelCenterSelectedOptions.some((s) => {
          return s === a.parcelCenterLocationName;
        });
      });
    }

    //ClientName Filter
    if (clientNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return clientNameSelectedOptions.some((s) => {
          return s === a.shipmentClientName;
        });
      });
    }

    //Status Filter
    if (statusSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return statusSelectedOptions.some((s) => {
          return s === a.status;
        });
      });
    }

    //RecipientCity Filter
    if (recipientCitySelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientCitySelectedOptions.some((s) => {
          return s === a.shipmentRecipientCity;
        });
      });
    }

    //RecipientState Filter
    if (recipientStateSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientStateSelectedOptions.some((s) => {
          return s === a.shipmentRecipientState;
        });
      });
    }

    //RecipientZip Filter
    if (recipientZipSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientZipSelectedOptions.some((s) => {
          return s === a.shipmentRecipientZip;
        });
      });
    }

    //Tracking Filter
    if (trackingNumberFilter) {
      if (trackingNumberFilter.includes(",")) {
        const trackingNumbers = trackingNumberFilter.split(",");
        newData = newData.filter((a) => {
          return trackingNumbers.some((s) => {
            return s === a.trackingNumber;
          });
        });
      } else {
        newData = newData.filter((x) =>
          x.trackingNumber
            .toLowerCase()
            .includes(trackingNumberFilter.toLowerCase())
        );
      }
    }

    if (statusFromDate && statusToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.statusDate).toLocaleDateString() >=
            statusFromDate.toLocaleDateString() &&
          new Date(x.statusDate).toLocaleDateString() <=
            statusToDate.toLocaleDateString()
      );
    }

    if (deliveryFromDate && deliveryToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.deliveryDate).toLocaleDateString() >=
            deliveryFromDate.toLocaleDateString() &&
          new Date(x.deliveryDate).toLocaleDateString() <=
            deliveryToDate.toLocaleDateString()
      );
    }

    if (updatedOnFromDate && updatedOnToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.updatedOn).toLocaleDateString() >=
            updatedOnFromDate.toLocaleDateString() &&
          new Date(x.deliveryDate).toLocaleDateString() <=
            updatedOnToDate.toLocaleDateString()
      );
    }
    setFilteredData(newData);
  }, [
    returnToStationData,
    setFilteredData,
    trackingNumberFilter,
    clientNameSelectedOptions,
    parcelCenterSelectedOptions,
    statusSelectedOptions,
    recipientCitySelectedOptions,
    recipientStateSelectedOptions,
    recipientZipSelectedOptions,
    statusFromDate,
    statusToDate,
    deliveryFromDate,
    deliveryToDate,
    updatedOnFromDate,
    updatedOnToDate,
  ]);

  // ***** Handlers *****
  const handleTrackingNumberChange = (e: any) => {
    let trackingNumbers = e.target.value;
    if (
      trackingNumbers.includes(" ") ||
      trackingNumbers.includes("\t") ||
      trackingNumbers.includes(",")
    ) {
      setTrackingNumberFilter(
        trackingNumbers.split(" ").join(",").split("\t").join(",")
      );
    } else {
      setTrackingNumberFilter(trackingNumbers);
    }
  };

  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === RTS_COLUMN_NAME.CLIENT_NAME) {
      return setClientNameSelectedOptions([]);
    }
    if (removedColumnName === RTS_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      return setParcelCenterSelectedOptions([]);
    }
    if (removedColumnName === RTS_COLUMN_NAME.PACKAGE_STATUS) {
      return setStatusSelectedOptions([]);
    }
    if (removedColumnName === RTS_COLUMN_NAME.RECIPIENT_CITY) {
      return setRecipientCitySelectedOptions([]);
    }
    if (removedColumnName === RTS_COLUMN_NAME.RECIPIENT_STATE) {
      return setRecipientStateSelectedOptions([]);
    }
    if (removedColumnName === RTS_COLUMN_NAME.RECIPIENT_ZIP) {
      return setRecipientZipSelectedOptions([]);
    }
    if (removedColumnName === RTS_COLUMN_NAME.STATUS_DATE) {
      setStatusFromDate(null);
      setStatusToDate(null);
      return;
    }
    if (removedColumnName === RTS_COLUMN_NAME.LAST_SCHEDULED_DELIVERY) {
      setDeliveryFromDate(null);
      setDeliveryToDate(null);
      return;
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];

    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === RTS_COLUMN_NAME.CLIENT_NAME) {
        setClientNameSelectedOptions([]);
      }
      if (oldColumnName === RTS_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        setParcelCenterSelectedOptions([]);
      }
      if (oldColumnName === RTS_COLUMN_NAME.PACKAGE_STATUS) {
        setStatusSelectedOptions([]);
      }
      if (oldColumnName === RTS_COLUMN_NAME.RECIPIENT_CITY) {
        setRecipientCitySelectedOptions([]);
      }
      if (oldColumnName === RTS_COLUMN_NAME.RECIPIENT_STATE) {
        setRecipientStateSelectedOptions([]);
      }
      if (oldColumnName === RTS_COLUMN_NAME.RECIPIENT_ZIP) {
        setRecipientZipSelectedOptions([]);
      }
      if (oldColumnName === RTS_COLUMN_NAME.STATUS_DATE) {
        setStatusFromDate(null);
        setStatusToDate(null);
      }
      if (oldColumnName === RTS_COLUMN_NAME.LAST_SCHEDULED_DELIVERY) {
        setDeliveryFromDate(null);
        setDeliveryToDate(null);
      }
    }
    if (value === RTS_COLUMN_NAME.CLIENT_NAME) {
      newInputList[index].displayColumnName = RTS_LABEL_NAME.CLIENT_NAME;
    }
    if (value === RTS_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      newInputList[index].displayColumnName =
        RTS_LABEL_NAME.PARCEL_CENTER_LOCATION_NAME;
    }
    if (value === RTS_COLUMN_NAME.PACKAGE_STATUS) {
      newInputList[index].displayColumnName = RTS_LABEL_NAME.PACKAGE_STATUS;
    }
    if (value === RTS_COLUMN_NAME.STATUS_DATE) {
      newInputList[index].displayColumnName = RTS_LABEL_NAME.STATUS_DATE;
    }
    if (value === RTS_COLUMN_NAME.LAST_SCHEDULED_DELIVERY) {
      newInputList[index].displayColumnName =
        RTS_LABEL_NAME.LAST_SCHEDULED_DELIVERY;
    }
    if (value === RTS_COLUMN_NAME.UPDATED_ON) {
      newInputList[index].displayColumnName = RTS_LABEL_NAME.UPDATED_ON;
    }
    if (value === RTS_COLUMN_NAME.RECIPIENT_CITY) {
      newInputList[index].displayColumnName = RTS_LABEL_NAME.RECIPIENT_CITY;
    }
    if (value === RTS_COLUMN_NAME.RECIPIENT_STATE) {
      newInputList[index].displayColumnName = RTS_LABEL_NAME.RECIPIENT_STATE;
    }
    if (value === RTS_COLUMN_NAME.RECIPIENT_ZIP) {
      newInputList[index].displayColumnName = RTS_LABEL_NAME.RECIPIENT_ZIP;
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleReset = (e: any) => {
    setTrackingNumberFilter("");
    setClientNameSelectedOptions([]);
    setParcelCenterSelectedOptions([]);
    setStatusSelectedOptions([]);
    setStatusFromDate(null);
    setStatusToDate(null);
    setDeliveryFromDate(null);
    setDeliveryToDate(null);
    setUpdatedOnFromDate(null);
    setUpdatedOnToDate(null);
    setRecipientCitySelectedOptions([]);
    setRecipientStateSelectedOptions([]);
    setRecipientZipSelectedOptions([]);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === RTS_COLUMN_NAME.CLIENT_NAME) {
      return setClientNameSelectedOptions(value);
    }
    if (columnName === RTS_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      return setParcelCenterSelectedOptions(value);
    }
    if (columnName === RTS_COLUMN_NAME.PACKAGE_STATUS) {
      return setStatusSelectedOptions(value);
    }
    if (columnName === RTS_COLUMN_NAME.RECIPIENT_CITY) {
      return setRecipientCitySelectedOptions(value);
    }
    if (columnName === RTS_COLUMN_NAME.RECIPIENT_STATE) {
      return setRecipientStateSelectedOptions(value);
    }
    if (columnName === RTS_COLUMN_NAME.RECIPIENT_ZIP) {
      return setRecipientZipSelectedOptions(value);
    }
  };

  const handleFromDateChange = (value: Date | null, columnName: string) => {
    if (columnName === RTS_COLUMN_NAME.STATUS_DATE) {
      return setStatusFromDate(value);
    }
    if (columnName === RTS_COLUMN_NAME.LAST_SCHEDULED_DELIVERY) {
      return setDeliveryFromDate(value);
    }
    if (columnName === RTS_COLUMN_NAME.UPDATED_ON) {
      return setUpdatedOnFromDate(value);
    }
  };

  const handleToDateChange = (value: Date | null, columnName: string) => {
    if (columnName === RTS_COLUMN_NAME.STATUS_DATE) {
      return setStatusToDate(value);
    }
    if (columnName === RTS_COLUMN_NAME.LAST_SCHEDULED_DELIVERY) {
      return setDeliveryToDate(value);
    }
    if (columnName === RTS_COLUMN_NAME.UPDATED_ON) {
      return setUpdatedOnToDate(value);
    }
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Return To Station Packages</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {RTS_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  {COLUMN_WITH_DATE.includes(inputList[index].columnName) ? (
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        {/* <MuiDateRangePicker /> */}
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} From Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleFromDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedFromDate(
                            inputList[index].columnName
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} To Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleToDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedToDate(inputList[index].columnName)}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <RTSAutoCompleteFilter
                      columnName={inputList[index].columnName}
                      displayColumnName={inputList[index].displayColumnName}
                      options={getFilterOptions(inputList[index].columnName)}
                      value={getSelectedOptions(inputList[index].columnName)}
                      onChange={(event: any, selectedValue: string[]) => {
                        handleSelectedOption(
                          event,
                          inputList[index].columnName,
                          selectedValue
                        );
                      }}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
            {/* Tracking Number Filter */}
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  key={"trackingNumber"}
                  margin="normal"
                  id="trackingNumber"
                  label="Tracking Number"
                  name="trackingNumber"
                  size="small"
                  fullWidth
                  value={trackingNumberFilter}
                  onChange={handleTrackingNumberChange}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default RTSDataFilter;
